import { ConversationLoadingSkeleton } from "@circle-react-shared/AiConversation/thread/ConversationLoadingSkeleton";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import {
  aiAgentQueries,
  moveMemberAvatarToFront,
} from "@circle-react/lib/queries/aiAgents";
import { communityInboxChatQueries } from "@circle-react/lib/queries/communityInboxChats";
import { useEffect } from "react";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import type { TiptapContent } from "@/react/components/SettingsApp/CommunityInbox/BotChatRoom/MessageBox";
import { useCommunityChat } from "@/react/components/SettingsApp/CommunityInbox/hooks/useCommunityChat";
import { useCreateBotChat } from "@/react/components/SettingsApp/CommunityInbox/hooks/useCreateBotChat";

import { AvatarComposer } from "../conversation/AvatarComposer";
import { AvatarConversationEmptyState } from "../conversation/AvatarConversationEmptyState";
import { AvatarConversationView } from "../conversation/AvatarConversationView";
import { buildQuestionContent } from "../conversation/buildQuestionContent";
import { useAvatarConversationPanelStore } from "../useAvatarConversationPanelStore";
import { useMarkAvatarChatAsRead } from "../useMarkAvatarChatAsRead";
import { AvatarsView } from "./AvatarsView";
import { Header } from "./Header";

export const AvatarConversationPanel = () => {
  const activeChatId = useAvatarConversationPanelStore(s => s.activeChatId);
  const newConversationAvatarId = useAvatarConversationPanelStore(
    s => s.newConversationAvatarId,
  );
  const view = useAvatarConversationPanelStore(s => s.view);
  const startNewConversation = useAvatarConversationPanelStore(
    s => s.startNewConversation,
  );
  const setActiveChatId = useAvatarConversationPanelStore(
    s => s.setActiveChatId,
  );
  const { createChat } = useCreateBotChat();
  const { fetchChat } = useCommunityInboxStore();
  const { data: latestChat, isPending: isLatestChatLoading } = useQuery(
    communityInboxChatQueries.latestAvatarChat(),
  );
  const hasSelectedAvatar = newConversationAvatarId !== null;
  const { data: avatars, isPending: isAvatarsPending } = useQuery({
    ...aiAgentQueries.memberAvatars(),
    enabled: hasSelectedAvatar,
  });
  const { currentChat: activeChat, isChatLoading: isActiveChatLoading } =
    useCommunityChat(activeChatId ?? 0, true);

  // Puts the chat in the member store, where websocket updates (is_processing)
  // are applied; the fetched copy useCommunityChat falls back to never changes.
  useEffect(() => {
    if (activeChatId) void fetchChat(activeChatId, "member");
  }, [activeChatId, fetchChat]);

  useMarkAvatarChatAsRead(
    view === "avatars" ? null : activeChatId,
    activeChat?.unread_messages_count,
  );

  const hasActiveChat = activeChatId !== null;
  const isSelectedAvatarLoading = hasSelectedAvatar && isAvatarsPending;
  const isLoading = hasActiveChat
    ? isActiveChatLoading
    : isLatestChatLoading || isSelectedAvatarLoading;
  const chat = (hasActiveChat && activeChat) || latestChat;
  const selectedAvatar = avatars?.find(
    avatar => avatar.id === newConversationAvatarId,
  );
  const isNewConversationWithSelectedAvatar =
    hasSelectedAvatar && !hasActiveChat;
  const isSelectedAvatarUnavailable =
    isNewConversationWithSelectedAvatar && !isAvatarsPending && !selectedAvatar;
  const agentProfile = isNewConversationWithSelectedAvatar
    ? selectedAvatar
    : chat?.community_bot_profile;
  const agentName = agentProfile?.name || t("community_inbox.agent");
  const isNewConversationWidgetOff =
    !hasActiveChat && agentProfile?.allow_widget_conversations === false;

  const handleStartConversation = async ({ body }: TiptapContent) => {
    if (!agentProfile) return;
    const newChat = await createChat({ id: agentProfile.id, body });
    moveMemberAvatarToFront(agentProfile.id);
    setActiveChatId(newChat.id);
  };

  if (
    view === "avatars" ||
    isSelectedAvatarUnavailable ||
    isNewConversationWidgetOff
  ) {
    return <AvatarsView />;
  }

  return (
    <>
      <Header
        name={isLoading ? undefined : agentName}
        title={hasActiveChat ? activeChat?.title : undefined}
        avatarId={agentProfile?.id}
        avatarUrl={agentProfile?.avatar_url}
      />
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {hasActiveChat && (
          <AvatarConversationView
            key={activeChatId}
            id={activeChatId}
            isDms
            onRestart={() =>
              agentProfile && startNewConversation(agentProfile.id)
            }
          />
        )}
        {!hasActiveChat && isLoading && <ConversationLoadingSkeleton />}
        {!hasActiveChat && !isLoading && (
          <AvatarConversationEmptyState
            agentName={agentName}
            agentProfile={agentProfile}
            onSelectQuestion={question =>
              handleStartConversation(buildQuestionContent(question))
            }
            composer={
              <AvatarComposer
                agentName={agentName}
                onSubmit={handleStartConversation}
              />
            }
          />
        )}
      </div>
    </>
  );
};
